import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { atomInfoCampaignV1 } from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import {
  atomActionTypeName,
  atomRestrictions,
} from '@/src/modules/CampaignsModule/atoms/createActionsAtom';
import { ACTION_CREATE_MESSAGE } from '@/src/modules/CampaignsModule/constants';

export const useCreateAction = (campaignId: string) => {
  const service = useService();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [createActionNameModal, setCreateActionNameModal] = useState(false);
  const [createActionIsLoading, setCreateActionIsLoading] = useState(false);
  const setInfoCampaignV1 = useSetAtom(atomInfoCampaignV1);
  const actionTypeName = useAtomValue(atomActionTypeName);
  const [restrictions, setRestrictions] = useAtom(atomRestrictions);

  const handleOpenCreateActionNameModal = useCallback(() => {
    setCreateActionNameModal(true);
  }, []);

  const handleCloseCreateItem = useCallback(() => {
    setCreateActionNameModal(false);
  }, []);

  const handleSubmit = useCallback(
    async (actionName: string, campaignName: string) => {
      setCreateActionIsLoading(true);
      actionName = actionName.trim();
      try {
        const response = await service.createCampaignAction(
          actionName,
          actionTypeName,
          campaignId ?? ''
        );
        if (response.message === ACTION_CREATE_MESSAGE) {
          const actionId = response.id;

          toast({
            title: t('CREATE_ACTIONS_MODAL.NOTIFICATION.actionCreateSuccess.title'),
            body: t('CREATE_ACTIONS_MODAL.NOTIFICATION.actionCreateSuccess.body'),
          });
          setInfoCampaignV1({
            campaign: {
              id: parseInt(campaignId || '0'),
              name: campaignName,
            },
            action: {
              id: Number(actionId),
              name: actionName,
            },
          });
          navigate(`${actionTypeName}/${actionId}`);
        }
      } catch (e) {
        toast({
          variant: 'error',
          title: t('CREATE_ACTIONS_MODAL.NOTIFICATION.actionCreateError.title'),
          body: (e as Error).message,
        });
      }
      setCreateActionIsLoading(false);
      handleCloseCreateItem();
    },
    [actionTypeName, campaignId, handleCloseCreateItem, navigate, service, setInfoCampaignV1, t]
  );

  const getRestrictions = useCallback(async () => {
    const response = await service.getRestrictions();
    setRestrictions(response);
  }, [service, setRestrictions]);

  useEffect(() => {
    if (restrictions !== null) return;

    void getRestrictions();
  }, [getRestrictions, restrictions]);

  return {
    handleSubmit,
    handleOpenCreateActionNameModal,
    createActionIsLoading,
    createActionNameModal,
    handleCloseCreateItem,
    restrictions,
  };
};
